/**
 * Follow-up drawer — which alert shows (drawer redesign Phase 6a, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-drawer-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * ── SOURCE OF TRUTH: TODAY'S DRAWER ────────────────────────────────────────────────────────────
 * Written from CreateFollowup.vue (FROZEN): the alert box is ONE `v-if / v-else-if` chain
 * (template :266–420), so exactly one alert shows, in this order:
 *   1. recallScheduled  recall queue && the patient's own recall appointment is 0–60 days out
 *   2. notResponsible   not the responsible employee — HIDDEN while the Collect Payment form is open
 *   3. doNotFollowup    first task's TeamioFollowupStatusID is 3/1/5, or the parent task is stopped
 *   4. maxAttempts      "All Done!" — attempts made ≥ the office maximum
 *   5. alreadyToday     a follow-up status was already saved today
 *   6. earlyWizard      the follow-up date is in the future (or the wizard is on step 2)
 *   7. lastAttempt      this attempt is the last one the office allows
 * The conditions are computeds (:3314–3534); each function below is one of them, with the same
 * comparisons. Dates: the layer's mysql config returns DATE/DATETIME as strings, so moment's
 * `isSame(moment(), 'day')` is the leading 'YYYY-MM-DD' against `today` (same zone, caller's job).
 * Kept quirks: `undefined` reads as "now" (moment(undefined)), `null`/'' as invalid (false).
 * Fixed (bug 10): tasks without a parent row never throw (isDoNotFollowup, totalAttemptsOf).
 *
 * tests/component/FollowupDrawerAgreement.spec.js mounts the real CreateFollowup.vue and fails if
 * it renders a different alert than resolveDrawerAlert for the same data.
 */

export const ALERT_ORDER = ['recallScheduled', 'notResponsible', 'doNotFollowup', 'maxAttempts', 'alreadyToday', 'earlyWizard', 'lastAttempt'];

/** The queues where a manual assignment on the task row decides who may follow up (:3318, :3329). */
const ASSIGNABLE = ['AgingDetails', 'OutstandingPreAuthDetails', 'PatientBalancesDetails'];

/** The queues whose follow-up is never "too early" (:3524). */
const NEVER_EARLY = ['AgingDetails', 'OutstandingPreAuthDetails'];

/** The "Do Not Follow-up" alert's bullet list, by queue (template :288–299). */
export const DO_NOT_FOLLOWUP_REASONS = {
  patient: [
    'The patient has been marked to not follow-up',
    'The patient may have an active appointment scheduled',
    'The Administrator has decided to stop further follow-ups',
  ],
  claim: [
    'This claim has been marked with a Processed Status',
    'The Administrator has decided to stop further follow-ups',
  ],
  balance: [
    'This Patient Balance Follow-up has been marked as Do Not Follow-up',
  ],
};

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})(?:$|[ T])/;
const WALL_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/;

function toYmd(value) {
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    const p = n => (n < 10 ? `0${n}` : String(n));
    return `${value.getFullYear()}-${p(value.getMonth() + 1)}-${p(value.getDate())}`;
  }
  if (typeof value !== 'string') return null;
  const m = YMD_RE.exec(value);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/** moment(value).isSame(moment(), 'day'). */
export function isSameDay(value, today) {
  if (value === undefined) return true;
  if (value === null || value === '') return false;
  const ymd = toYmd(value);
  return ymd !== null && ymd === today;
}

/** moment(value).isSameOrBefore(moment(), 'day'). */
export function isSameOrBeforeDay(value, today) {
  if (value === undefined) return true;
  if (value === null || value === '') return false;
  const ymd = toYmd(value);
  return ymd !== null && ymd <= today;
}

/** Wall-clock milliseconds of a local DATE/DATETIME string (or a Date's local fields). */
function wallMs(value) {
  if (value instanceof Date) {
    return Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), value.getHours(), value.getMinutes(), value.getSeconds());
  }
  if (typeof value !== 'string') return NaN;
  const m = WALL_RE.exec(value);
  if (!m) return NaN;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0));
}

/**
 * moment(a).diff(moment(b), 'days'): whole days between two local times, truncated toward zero.
 * moment subtracts the zones' offset difference, so a DST change in between does not move it —
 * which is the difference of the two WALL clocks. `now` is the caller's local 'YYYY-MM-DDTHH:mm:ss'.
 */
export function daysBetween(a, now) {
  const d = (wallMs(a) - wallMs(now)) / 864e5;
  if (isNaN(d)) return NaN;
  return d < 0 ? Math.ceil(d) || 0 : Math.floor(d); // moment's absFloor (no -0)
}

/** [patientHasRecallScheduled, :3402] the patient's own row in the family recall set is booked 0–60 days out. */
export function hasRecallScheduled({ familyAppts, patNum, now } = {}) {
  if (patNum === null || patNum === undefined || !familyAppts || !(familyAppts.length > 0)) return false;
  const me = familyAppts.filter(p => p.PatNum === parseInt(patNum, 10))[0];
  if (!me || !me.AptDateTime) return false;
  const daysDiff = daysBetween(me.AptDateTime, now);
  return daysDiff <= 60 && daysDiff >= 0;
}

/**
 * [isResponsiblePersonMakingAttempt, :3314] `responsibleIds` = the setting split on '|' (or null).
 * Strict, untrimmed membership — today's drawer does NOT trim " 12 | 19 " (utils/permissions.js
 * isInResponsibleList does); kept. On claims / pre-auths / balances a manually assigned task row
 * (EmployeeManuallyAssigned === 1) wins: only its AssignedToEmpID may follow up.
 */
export function isResponsiblePerson({ detailPageName, responsibleIds, userID, assignmentRow } = {}) {
  if (!responsibleIds || !(responsibleIds.length > 0)) return false;
  if (ASSIGNABLE.indexOf(detailPageName) !== -1 && assignmentRow && typeof assignmentRow === 'object') {
    if (assignmentRow.EmployeeManuallyAssigned === 1) return assignmentRow.AssignedToEmpID === userID;
    return responsibleIds.includes(userID);
  }
  return responsibleIds.includes(userID);
}

/** [totalAttemptsMade, :3387] the parent task's Attempts (as stored — may be null), else 0. */
export function totalAttemptsOf(mainParentTask) {
  return mainParentTask ? mainParentTask.Attempts : 0;
}

/** [maxAttemptsReached, :3531] only when the office set a maximum. */
export function isMaxAttemptsReached(totalAttempts, maxFollowups) {
  if (!maxFollowups) return false;
  return totalAttempts >= parseInt(maxFollowups, 10);
}

/** [isLastAttempt, :3382] */
export function isLastAttempt(totalAttempts, maxFollowups) {
  if (!maxFollowups) return false;
  return totalAttempts + 1 === parseInt(maxFollowups, 10);
}

/**
 * [doNotFollowup, :3378] bug 10 FIXED (Nilay 2026-10-02): today reads
 * `mainParentTaskData.FollowupStopped` unguarded, so tasks with no parent row (and a first status
 * not 3/1/5) throw during render (:3379). Here a missing parent reads as "not stopped".
 */
export function isDoNotFollowup({ tasks, mainParentTask } = {}) {
  if (!tasks || !(tasks.length > 0)) return false;
  const t = tasks[0].TeamioFollowupStatusID;
  return t === 3 || t === 1 || t === 5 || !!(mainParentTask && mainParentTask.FollowupStopped === 1);
}

/**
 * [attemptsAllowed, :3446] false once a status was saved TODAY (the first task's StatusUpdatedAt).
 * Recall additionally refuses a patient whose recall is booked even before any task exists.
 * Settings not loaded → false (today: undefined).
 */
export function isAttemptAllowed({ detailPageName, settingsLoaded, tasks, statusUpdatedAtField, recallScheduled, today } = {}) {
  if (!settingsLoaded) return false;
  const none = !tasks || tasks.length === 0;
  const notToday = () => !!(tasks && tasks.length > 0 && !isSameDay(tasks[0][statusUpdatedAtField], today));
  if (detailPageName === 'OverdueRecallDetails') {
    if (!recallScheduled && none) return true;
    return notToday();
  }
  if (['unscheduledPatientDetails', 'BrokenAppointmentDetails', 'AgingDetails', 'OutstandingPreAuthDetails',
    'ProcessedPreAuthDetails', 'PatientBalancesDetails'].indexOf(detailPageName) !== -1) {
    return none ? true : notToday();
  }
  return false;
}

/** [followupAllowedToday, :3520] the first task's FollowupDate is today or earlier (never early on claims / pre-auths). */
export function isFollowupAllowedToday({ detailPageName, tasks, today } = {}) {
  if (!tasks || tasks.length === 0) return true;
  if (NEVER_EARLY.indexOf(detailPageName) !== -1) return true;
  return isSameOrBeforeDay(tasks[0].FollowupDate, today);
}

/** The "Do Not Follow-up" list variant (template :288–299). Processed pre-auths get the patient list. */
export function doNotFollowupVariant(detailPageName) {
  if (detailPageName === 'AgingDetails' || detailPageName === 'OutstandingPreAuthDetails') return 'claim';
  if (detailPageName === 'PatientBalancesDetails') return 'balance';
  return 'patient';
}

/**
 * Every condition the alert box and the method buttons read, from the drawer's data. Inputs are what
 * the container already has: `responsibleIds` / `maxFollowups` from drawerQueues, `today` /
 * `now` from the site clock ('YYYY-MM-DD' / 'YYYY-MM-DDTHH:mm:ss').
 */
export function alertConditions(input = {}) {
  const {
    detailPageName, settingsLoaded, responsibleIds, maxFollowups, statusUpdatedAtField, userID, patNum,
    today, now, tasks, mainParentTask, assignmentRow, familyAppts,
  } = input;
  const recallScheduled = detailPageName === 'OverdueRecallDetails' && hasRecallScheduled({ familyAppts, patNum, now });
  const totalAttempts = totalAttemptsOf(mainParentTask);
  const max = settingsLoaded ? maxFollowups : null;
  return {
    recallScheduled,
    isResponsible: isResponsiblePerson({ detailPageName, responsibleIds, userID, assignmentRow }),
    doNotFollowup: isDoNotFollowup({ tasks, mainParentTask }),
    totalAttempts,
    maxFollowups: max,
    maxAttemptsReached: isMaxAttemptsReached(totalAttempts, max),
    isLastAttempt: isLastAttempt(totalAttempts, max),
    // the recall-scheduled check feeds attemptsAllowed on recall only (:3452)
    attemptsAllowed: isAttemptAllowed({ detailPageName, settingsLoaded, tasks, statusUpdatedAtField, today, recallScheduled }),
    followupAllowedToday: isFollowupAllowedToday({ detailPageName, tasks, today }),
  };
}

/**
 * The ONE alert today's chain renders, or null. `wizardStep` is the early-follow-up wizard's step
 * (1 or 2); `collectPaymentFormVisible` hides the not-responsible alert while the payment form is open.
 */
export function resolveDrawerAlert(c = {}, { detailPageName, wizardStep = 1, collectPaymentFormVisible = false } = {}) {
  if (detailPageName === 'OverdueRecallDetails' && c.recallScheduled) return { key: 'recallScheduled' };
  if (!c.isResponsible && !collectPaymentFormVisible) return { key: 'notResponsible' };
  if (c.doNotFollowup) {
    const variant = doNotFollowupVariant(detailPageName);
    return { key: 'doNotFollowup', variant, reasons: DO_NOT_FOLLOWUP_REASONS[variant] };
  }
  if (c.maxAttemptsReached) return { key: 'maxAttempts' };
  if (!c.attemptsAllowed) return { key: 'alreadyToday' };
  if (!c.followupAllowedToday || wizardStep === 2) return { key: 'earlyWizard', step: wizardStep === 2 ? 2 : 1 };
  if (c.isLastAttempt) return { key: 'lastAttempt' };
  return null;
}

/**
 * [showAttempt's gate, :2307] whether Phone / Text / Email open a form at all. Separate from the
 * alert: the payment form ignores it, and the Phone button never looks disabled (see drawerMethods).
 */
export function attemptGateOpen(c = {}) {
  return !!(c.isResponsible && c.attemptsAllowed && !c.doNotFollowup && !c.maxAttemptsReached && c.followupAllowedToday);
}

/** The early-follow-up wizard accepts today or an earlier day only (:1896). */
export function wizardDateAllowed(dateYmd, today) {
  const ymd = toYmd(dateYmd);
  return ymd !== null && ymd <= today;
}
